import { Suspense, useEffect } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';

import { Loader } from '@flintmint/web-shared/components/loader';
import { getAuthUser } from '@flintmint/web-shared/hooks/use-auth-user';

import { Layout } from '@/components/layout';
import { RouteSkeleton } from '@/components/route-skeleton/route-skeleton';
import { appRoutes } from '@/constants/routes';
import { useFirebaseAuth } from '@/providers/firebase-provider';
import { warmUpFirebase } from '@/services/firebase-service';

import styles from './protected-routes.module.scss';

export const ProtectedRoutes = () => {
  const user = getAuthUser();
  const { isAuthenticated: isAuth0Authenticated, isLoading: isAuth0Loading } = useAuth0();
  const firebaseAuth = useFirebaseAuth();
  const navigate = useNavigate();

  useEffect(() => {
    warmUpFirebase();
  }, []);

  useEffect(() => {
    if (isAuth0Loading) return;

    if (!isAuth0Authenticated || !user.id) {
      navigate(appRoutes.login, { replace: false });
    }
  }, [isAuth0Authenticated, isAuth0Loading, user.id, navigate]);

  useEffect(() => {
    if (firebaseAuth.isLoading || !firebaseAuth.isAuthenticated) return;

    if (!firebaseAuth.isAdmin) {
      navigate(appRoutes.accessDenied, { replace: true });
    }
  }, [firebaseAuth.isLoading, firebaseAuth.isAuthenticated, firebaseAuth.isAdmin, navigate]);

  useEffect(() => {
    if (!firebaseAuth.denialReason) return;

    navigate(firebaseAuth.denialReason === 'email_not_verified' ? appRoutes.emailVerification : appRoutes.accessDenied, {
      replace: true,
    });
  }, [firebaseAuth.denialReason, navigate]);

  if (isAuth0Loading || !isAuth0Authenticated || !user.id) {
    return <Loader fullScreen />;
  }

  // isAdmin is only ever true alongside isAuthenticated.
  return (
    <Layout>
      {firebaseAuth.error ? (
        <div className={styles.error}>Authentication failed: {firebaseAuth.error}</div>
      ) : firebaseAuth.isLoading || !firebaseAuth.isAdmin ? (
        <RouteSkeleton />
      ) : (
        <Suspense fallback={<RouteSkeleton />}>
          <Outlet />
        </Suspense>
      )}
    </Layout>
  );
};
